import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import { Business as BusinessIcon, Description as DescriptionIcon } from '@mui/icons-material';
import api from '../../../services/api';
import { Auditoria, CreateAuditoriaDTO } from '../../../types/audit.types';
import auditService from '../../../services/auditService';
import { generateUUID } from '../../../utils/uuid';

interface CreateAuditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (auditoria: Auditoria) => void;
}

interface Negocio {
  id: number;
  name: string;
  address: string;
  active: boolean;
}

interface Formulario {
  id: number;
  name: string;
  description: string;
  active: boolean;
  total_questions?: number;
}

const CreateAuditDialog: React.FC<CreateAuditDialogProps> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [formularios, setFormularios] = useState<Formulario[]>([]);
  
  const [selectedNegocio, setSelectedNegocio] = useState<number | ''>('');
  const [selectedFormulario, setSelectedFormulario] = useState<number | ''>('');

  // Cargar negocios y formularios al abrir
  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // Reset al cerrar
      setSelectedNegocio('');
      setSelectedFormulario('');
      setError(null);
    }
  }, [open]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      // Solo cargar negocios inicialmente
      const negociosRes = await api.get('/business?active=true&limit=1000');

      // Backend devuelve { businesses: [], pagination: {} } para negocios
      const negociosData = negociosRes.data.businesses || negociosRes.data.data || negociosRes.data;

      setNegocios(Array.isArray(negociosData) ? negociosData : []);

      if (!Array.isArray(negociosData) || negociosData.length === 0) {
        setError('No hay negocios activos disponibles');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar datos');
      console.error('Error cargando datos:', err);
    } finally {
      setLoadingData(false);
    }
  };

  // Cargar formularios cuando se selecciona un negocio
  const loadFormulariosByNegocio = async (negocioId: number) => {
    try {
      setLoadingData(true);
      setError(null);

      // Formularios asignados a este negocio
      const response = await api.get(`/business/${negocioId}/forms?active=true`);

      // Backend devuelve { data: [], total: number }
      const formulariosData = response.data.data || response.data;

      setFormularios(Array.isArray(formulariosData) ? formulariosData : []);

      if (!Array.isArray(formulariosData) || formulariosData.length === 0) {
        setError('Este negocio no tiene formularios asignados');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar formularios');
      console.error('Error cargando formularios:', err);
      setFormularios([]);
    } finally {
      setLoadingData(false);
    }
  };

  // Manejar cambio de negocio
  const handleNegocioChange = (event: SelectChangeEvent<string | number>) => {
    const negocioId = event.target.value as number;
    setSelectedNegocio(negocioId);
    setSelectedFormulario(''); // Reset formulario
    setFormularios([]); // Limpiar formularios

    if (negocioId) {
      loadFormulariosByNegocio(negocioId);
    }
  };

  const handleCreate = async () => {
    if (!selectedNegocio || !selectedFormulario) {
      setError('Debe seleccionar un negocio y un formulario');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Generar UUID para offline_id (compatible con todos los navegadores)
      const offlineId = generateUUID();

      const data: CreateAuditoriaDTO = {
        business_id: selectedNegocio as number,
        form_id: selectedFormulario as number,
        offline_id: offlineId,
      };

      const response = await auditService.create(data);
      onSuccess(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear auditoría');
      console.error('Error creando auditoría:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedNegocioData = negocios.find(n => n.id === selectedNegocio);
  const selectedFormularioData = formularios.find(f => f.id === selectedFormulario);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
        }
      }}
    >
      <DialogTitle>
        📋 Nueva Auditoría
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Selecciona el negocio y el formulario para comenzar una nueva auditoría.
            </Typography>

            {/* Seleccionar Negocio */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="negocio-select-label" htmlFor="negocio-select-input">Negocio *</InputLabel>
              <Select
                labelId="negocio-select-label"
                id="negocio-select"
                value={selectedNegocio}
                label="Negocio *"
                onChange={handleNegocioChange}
                disabled={loading}
                inputProps={{
                  name: 'negocio',
                  id: 'negocio-select-input',
                }}
              >
                <MenuItem value="" disabled>
                  Selecciona un negocio
                </MenuItem>
                {negocios.map((negocio) => (
                  <MenuItem key={negocio.id} value={negocio.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">{negocio.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {negocio.address}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Seleccionar Formulario */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="formulario-select-label" htmlFor="formulario-select-input">Formulario *</InputLabel>
              <Select
                labelId="formulario-select-label"
                id="formulario-select"
                value={selectedFormulario}
                label="Formulario *"
                onChange={(e) => setSelectedFormulario(e.target.value as number)}
                disabled={loading || !selectedNegocio || loadingData}
                inputProps={{
                  name: 'formulario',
                  id: 'formulario-select-input',
                }}
              >
                <MenuItem value="" disabled>
                  {!selectedNegocio 
                    ? 'Primero selecciona un negocio' 
                    : loadingData 
                      ? 'Cargando formularios...'
                      : 'Selecciona un formulario'}
                </MenuItem>
                {formularios.map((formulario) => (
                  <MenuItem key={formulario.id} value={formulario.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">{formulario.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formulario.description || 'Sin descripción'}
                          {formulario.total_questions && ` • ${formulario.total_questions} preguntas`}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Resumen de selección */}
            {selectedNegocioData && selectedFormularioData && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: (theme) => theme.palette.grey[50],
                  borderRadius: 1 
                }}>
                  <Typography variant="subtitle2" gutterBottom>
                    📋 Resumen:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Negocio: <strong>{selectedNegocioData.name}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Formulario: <strong>{selectedFormularioData.name}</strong>
                  </Typography>
                  {selectedFormularioData.total_questions && (
                    <Typography variant="body2" color="text.secondary">
                      • Preguntas: <strong>{selectedFormularioData.total_questions}</strong>
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading || !selectedNegocio || !selectedFormulario || loadingData}
        >
          {loading ? 'Creando...' : 'Crear Auditoría'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAuditDialog;
